import {
  AttachmentCategoryEnum,
  isCustomerRole,
  UserRoleEnum,
} from "@nlc/shared-types";
import {
  ChevronLeft,
  ChevronRight,
  DownloadIcon,
  MessageSquare,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Overlay, useKeyboard } from "react-aria";

import type {
  ActionButtonsProps,
  MediaContentProps,
  MediaItem,
  MediaPreviewModalProps,
  ModalHeaderProps,
  NavigationControlsProps,
} from "@/components/media-preview-modal/mediaPreviewModal.types";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { Directions } from "@/components/media-preview-modal/mediaPreviewModal.types";
import { MediaViewer } from "@/components/media-viewer";
import { useDownloadAttachments } from "@/hooks/useDownloadAttachments.tsx";
import { useUser } from "@/hooks/useUser.tsx";

const MODAL_CONVERSION_POLL_OPTIONS = {
  maxAttempts: 6,
  pollIntervalMs: 10_000,
};

export function MediaPreviewModal({
  alt = "",
  currentItemId,
  defaultSelectedItem,
  mediaItems,
  onClose,
  onDownload,
  onFeedback,
  onNavigate,
  usePreview = true,
}: MediaPreviewModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { mutate: downloadItem } = useDownloadAttachments();

  const [currentIndex, setCurrentIndex] = useState(
    currentItemId
      ? getCurrentIndex(currentItemId, mediaItems)
      : getCurrentIndex(defaultSelectedItem, mediaItems),
  );

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  const currentItem: MediaItem = mediaItems[currentIndex] as MediaItem;

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(currentItem);

      return;
    }

    downloadItem({
      fallbackUrl: currentItem.url,
      fileId: Number(currentItem.id),
    });
  };

  const handleNavigate = useCallback(
    (direction: Directions) => {
      if (mediaItems.length < 2 || currentIndex === -1) return;

      let newIndex: number;

      if (direction === Directions.PREV) {
        newIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
      } else {
        newIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
      }

      const newItem = mediaItems[newIndex];

      setCurrentIndex(newIndex);

      if (onNavigate && newItem?.id) onNavigate(newItem.id);
    },
    [currentIndex, mediaItems, onNavigate, setCurrentIndex],
  );

  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      if (e.key === "Escape") {
        onClose();
      }
      if (e.key === "ArrowLeft") {
        handleNavigate(Directions.PREV);
      }
      if (e.key === "ArrowRight") {
        handleNavigate(Directions.NEXT);
      }
    },
  });

  return (
    <Overlay>
      <div
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        data-react-aria-top-layer="true"
        tabIndex={0}
        {...keyboardProps}
        onClick={onClose}
        ref={modalRef}
      >
        <ModalHeader title={currentItem?.name || ""} />

        <ActionButtons
          currentItem={currentItem}
          onClose={onClose}
          onDownload={handleDownload}
          onFeedback={onFeedback}
        />

        <MediaContent
          alt={alt}
          attachmentId={Number(currentItem?.id)}
          extension={currentItem.extension}
          src={currentItem?.url || ""}
          usePreview={usePreview}
        />

        {mediaItems.length > 1 && (
          <NavigationControls
            currentIndex={currentIndex}
            onNavigate={handleNavigate}
            totalItems={mediaItems.length}
          />
        )}
      </div>
    </Overlay>
  );
}

function ModalHeader({ title }: ModalHeaderProps) {
  return (
    <div
      className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-white text-lg font-medium" title={title}>
        {title}
      </p>
    </div>
  );
}

function ActionButtons({
  currentItem,
  onClose,
  onDownload,
  onFeedback,
}: ActionButtonsProps) {
  const { data: user } = useUser();

  if (!user?.user.roleId) return null;

  const canShowFeedback =
    onFeedback &&
    (isCustomerRole(user?.user?.roleId) ||
      user?.user?.roleId === UserRoleEnum.SuperAdmin) &&
    currentItem?.category === AttachmentCategoryEnum.MY_FILE;

  return (
    <div
      className="absolute top-4 right-4 flex items-center gap-2 z-50"
      onClick={(e) => e.stopPropagation()}
    >
      {canShowFeedback && (
        <Button
          className="cursor-pointer flex items-center gap-4"
          onPress={() => {
            onClose();
            onFeedback(currentItem.id);
          }}
          type="button"
          variant="primary"
        >
          <AccessibleIcon>
            <MessageSquare size={24} />
          </AccessibleIcon>
          <span className="text-lg font-bold text-white ml-2">Feedback</span>
        </Button>
      )}

      <Button
        className="text-white hover:text-green-400 transition-colors p-2 bg-black/30 rounded-lg backdrop-blur-sm"
        isIconOnly
        onPress={() => {
          onDownload();
        }}
        type="button"
        variant="tertiary"
      >
        <AccessibleIcon aria-label="Download">
          <DownloadIcon size={24} />
        </AccessibleIcon>
      </Button>

      <Button
        className="text-white hover:text-red-400 transition-colors p-2 bg-black/30 rounded-lg backdrop-blur-sm"
        isIconOnly
        onPress={onClose}
        type="button"
        variant="tertiary"
      >
        <AccessibleIcon aria-label="Close">
          <X size={24} />
        </AccessibleIcon>
      </Button>
    </div>
  );
}

function MediaContent({
  alt,
  attachmentId,
  extension,
  src,
  usePreview,
}: MediaContentProps) {
  return (
    <div className="w-full h-[80vh] flex items-center justify-center">
      <MediaViewer
        alt={alt}
        attachmentId={attachmentId}
        conversionPollOptions={MODAL_CONVERSION_POLL_OPTIONS}
        extension={extension}
        showFullscreenToggle={false}
        showZoomControls={true}
        src={src}
        usePreview={usePreview}
      />
    </div>
  );
}

function NavigationControls({
  currentIndex,
  onNavigate,
  totalItems,
}: NavigationControlsProps) {
  return (
    <div
      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-4 bg-black/50 backdrop-blur-md rounded-lg px-4 py-2">
        <Button
          className="text-white hover:text-blue-400 transition-colors p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          isIconOnly
          onPress={() => onNavigate(Directions.PREV)}
          type="button"
          variant="tertiary"
        >
          <AccessibleIcon aria-label="Previous image">
            <ChevronLeft size={20} />
          </AccessibleIcon>
        </Button>

        <div className="text-white text-sm font-medium min-w-[60px] text-center">
          {currentIndex + 1} of {totalItems}
        </div>

        <Button
          className="text-white hover:text-blue-400 transition-colors p-2 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          isIconOnly
          onPress={() => onNavigate(Directions.NEXT)}
          type="button"
          variant="tertiary"
        >
          <AccessibleIcon aria-label="Next image">
            <ChevronRight size={20} />
          </AccessibleIcon>
        </Button>
      </div>
    </div>
  );
}

function getCurrentIndex(itemId: string | undefined, mediaItems: MediaItem[]) {
  if (!itemId) return 0;

  return mediaItems.findIndex((item) => item.id === itemId);
}
